import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { hairline, radius, rem, space } from '@gbi/ui';
import BottomDrawer from '../Drawer/Drawer';
import Button from '../Button/Button';
import tokens from '../../assets/styles/tokens';
import { GUTTER, ModalCloseIcon, wizardButtonCss } from './Common';
import { ReactComponent as InfoBlueIcon } from '../../assets/images/priceAlerts/info-blue.svg';

// Styled to the designer's "How 'Buy at Target Price' Works" frame.
const DrawerTitle = styled.span`
  ${tokens.type.title[400]}
  color: ${tokens.color.text.primary};
`;

const Subtitle = styled.p`
  ${tokens.type.body[300]}
  color: ${tokens.color.text.label};
  margin-bottom: ${rem(space[24])};
`;

// One step: its badge, title and body 6px apart; steps 16px apart.
const StepRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(space[6])};
  margin-bottom: ${rem(space[16])};
`;

// "Step N" chip: label/200 on the focus slate, sized by its padding.
const StepBadge = styled.span`
  padding: ${rem(space[2])} ${rem(space[6])};
  border-radius: ${radius.sm};
  background: ${tokens.color.border.focus};
  ${tokens.type.label[200]}
  color: ${tokens.color.text.inverse};
`;

const StepTitle = styled.p`
  ${tokens.type.title[300]}
  color: ${tokens.color.text.primary};
  margin: 0;
`;

const StepBody = styled.p`
  ${tokens.type.body[300]}
  color: ${tokens.color.text.label};
  margin: 0;
`;

// 24px under the last step (its own 16px margin plus 8px).
const Callout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(space[4])};
  padding: ${rem(space[6])} ${rem(space[8])};
  margin-top: ${rem(space[8])};
  background: ${tokens.color.surface.sunken};
  border: ${hairline} solid ${tokens.color.border.default};
  border-radius: ${radius.base};
`;

// The 15px glyph with 2px around it fills the frame's 18px icon box.
const CalloutIcon = styled(InfoBlueIcon)`
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  margin: ${rem(space[2])} ${rem(space[2])} 0;
`;

const CalloutText = styled.p`
  ${tokens.type.body[300]}
  color: ${tokens.color.text.label};
  margin: 0;
`;

// "Let's Go": the wizard's full-width button text, in the outlined style.
const IntroFooter = styled.div`
  && button {
    width: 100%;
    ${wizardButtonCss}
  }
`;

// Intro drawer for the Buy-at-Target-Price order-entry step, mirroring
// PriceAlerts/AlertInfoDrawer but with the two-step "how it works" design.
// Auto-opened once by TargetPriceOrderEntry; "Let's Go" just closes it.
const TargetPriceInfoDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={<DrawerTitle>{t('gtt.targetPriceIntro.title')}</DrawerTitle>}
      closeIcon={<ModalCloseIcon />}
      // The frame is 502px tall at 320; 90% of a 568px screen holds it.
      maxHeight="90%"
      headerPadding={`${GUTTER} ${GUTTER} ${rem(space[4])}`}
      contentPadding={`0 ${GUTTER}`}
      footer={
        <IntroFooter>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            type="button"
            onClick={onClose}
            data-cy="gtt-intro-lets-go"
          >
            {t('gtt.common.letsGo')}
          </Button>
        </IntroFooter>
      }
    >
      <Subtitle>{t('gtt.targetPriceIntro.subtitle')}</Subtitle>

      <StepRow>
        <StepBadge>{t('gtt.targetPriceIntro.step1Label')}</StepBadge>
        <StepTitle>{t('gtt.targetPriceIntro.step1Title')}</StepTitle>
        <StepBody>{t('gtt.targetPriceIntro.step1Body')}</StepBody>
      </StepRow>

      <StepRow>
        <StepBadge>{t('gtt.targetPriceIntro.step2Label')}</StepBadge>
        <StepTitle>{t('gtt.targetPriceIntro.step2Title')}</StepTitle>
        <StepBody>{t('gtt.targetPriceIntro.step2Body')}</StepBody>
      </StepRow>

      <Callout>
        <CalloutIcon />
        <CalloutText>{t('gtt.targetPriceIntro.callout')}</CalloutText>
      </Callout>
    </BottomDrawer>
  );
};

TargetPriceInfoDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default TargetPriceInfoDrawer;
